import { generateGuid } from '@/utils/generateGuid';
import { getPartnerIdFromStorage } from '@/utils/storage/partnerId';
import { getMoscowDate } from '@/utils/payment/getMoscowDate';
import { IBitrixItem } from '@/utils/payment/paymentItemsBuilder';
import { getPaymentProvider } from '@/utils/payment/paymentProvider';
import { loggerSendingData } from './logger.action';
import { feedbackEmail } from '@/redux/actions/feedback.action';
import { axiosBitrixInstance, axiosInstance } from '@/resources/requestInfo';

export async function createPaymentLink(
  setUrl: (url: string) => void,
  totalPrice: number,
  phone: string,
  email: string,
  cardId: string | null,
  setPaymentId: (id: string) => void,
  description: string,
  items: any[],
  setGuid: (guid: string) => void,
  itemsWithoutKassa: IBitrixItem[],
  setConfirmationToken: (token: string) => void
): Promise<{
  success: boolean;
  paymentId?: string;
  confirmationToken?: string;
  confirmationUrl?: string;
  status?: string;
  hasSavedCard?: boolean;
  error?: any;
  errorMessage?: string;
}> {
  try {
    const partnerId = getPartnerIdFromStorage();

    const guid = generateGuid({ phone, email, items });
    setGuid(guid);

    const totalPriceFormatted = parseFloat(totalPrice.toFixed(2)).toFixed(2);

    const paymentMethodId = cardId && typeof cardId === 'string' ? cardId : null;

    // Формируем customer объект согласно требованиям ЮKassa (П1075)
    const customerData: { phone?: string; email?: string } = {};
    if (phone) customerData.phone = phone;
    if (email) customerData.email = email;

    const provider = getPaymentProvider();

    const body: any = {
      amount: {
        value: totalPriceFormatted,
        currency: 'RUB',
      },
      description: description,
      receipt: {
        customer: customerData,
        items: items,
      },
      metadata: {
        order_id: guid,
        partner_id: partnerId || '',
        phone: phone || '',
      },
      provider,
    };

    if (paymentMethodId) {
      body.payment_method_id = paymentMethodId;
    }

    await loggerSendingData({
      type: 'createPaymentLink',
      data: {
        provider,
        paymentWithCard: !!paymentMethodId,
        data: body,
      },
    });

    // Вызов бэкенда вместо прямого обращения к YooKassa (решает CORS)
    const res = await axiosInstance.post('/payments/create', body);

    const paymentIdFromResponse = res.data?.id;
    const paymentStatus = res.data?.status;
    const confirmationToken = res.data?.confirmation_token;
    const confirmationUrl = res.data?.confirmation_url;

    setPaymentId(paymentIdFromResponse);

    if (!partnerId) {
      await loggerSendingData({
        type: 'prepaymentBitrixPaymentObjectSendError',
        data: 'PartnerId is missing',
      });
      return {
        success: false,
        error: 'PartnerId is missing',
        errorMessage: 'Ошибка авторизации. Пожалуйста, войдите в систему заново.',
      };
    }

    if (paymentIdFromResponse) {
      try {
        await prepaymentBitrixPaymentObjectSend(guid, itemsWithoutKassa, false, paymentIdFromResponse);
      } catch (prepaymentError: any) {
        console.error('Ошибка отправки препэймента:', prepaymentError);
        await loggerSendingData({
          type: 'prepaymentBitrixPaymentObjectSendError',
          data: prepaymentError,
        });

        // Отменяем платеж, так как препэймент в 1С не прошел
        try {
          await cancelPayment(paymentIdFromResponse);
        } catch (cancelError) {
          console.error('Error during payment cancellation:', cancelError);
        }

        await feedbackEmail({
          message: `
            Ошибка препэймента 1С (платеж отменен):
            Ошибка: ${prepaymentError.name || 'Unknown error'}
            Данные ошибки: ${JSON.stringify(prepaymentError.response?.data)}
            Сообщение ошибки: ${prepaymentError.message}
            Payment ID: ${paymentIdFromResponse}
            GUID: ${guid}
          `,
        });

        return {
          success: false,
          error: prepaymentError.response?.data || prepaymentError.message,
          errorMessage: 'Ошибка отправки данных в 1С. Платеж отменен. Попробуйте снова.',
        };
      }
    }

    // Для виджета ЮKassa возвращаем confirmation_token
    if (confirmationToken) {
      setConfirmationToken(confirmationToken);
      await loggerSendingData({
        type: 'createPaymentLinkSuccess',
        data: { paymentId: paymentIdFromResponse, hasToken: true },
      });

      return {
        success: true,
        paymentId: paymentIdFromResponse,
        confirmationToken: confirmationToken,
        status: paymentStatus,
        hasSavedCard: !!paymentMethodId,
      };
    }

    // Тинькофф (Т-Касса): нет токена виджета — открываем платёжную форму по URL
    if (provider === 'tinkoff' && confirmationUrl) {
      setUrl(confirmationUrl);
      await loggerSendingData({
        type: 'createPaymentLinkSuccess',
        data: { provider, paymentId: paymentIdFromResponse, paymentUrl: confirmationUrl },
      });

      return {
        success: true,
        paymentId: paymentIdFromResponse,
        confirmationUrl,
        status: paymentStatus,
        hasSavedCard: false,
      };
    }

    // Для сохранённых карт может не быть токена (оплата без подтверждения)
    if (paymentMethodId) {
      setUrl('NO_URL_NEEDED_FOR_SAVED_CARD');
      await loggerSendingData({
        type: 'createPaymentLinkSuccess',
        data: { paymentId: paymentIdFromResponse, savedCard: true },
      });

      return {
        success: true,
        paymentId: paymentIdFromResponse,
        status: paymentStatus,
        hasSavedCard: true,
      };
    }

    await loggerSendingData({
      type: 'createPaymentLinkError',
      data: res.data,
    });

    return {
      success: false,
      error: res.data,
      errorMessage: 'Не удалось получить токен для оплаты',
    };
  } catch (err: any) {
    console.error('Payment creation error:', err.response?.data || err.message);

    await loggerSendingData({
      type: 'createPaymentLinkError',
      data: err.response?.data || err.message,
    });

    await feedbackEmail({
      message: `
        Ошибка создания платежа:
        Ошибка: ${err.name || 'Unknown error'}
        Данные ошибки: ${JSON.stringify(err.response?.data)}
        Сообщение ошибки: ${err.message}
      `,
    });

    return {
      success: false,
      error: err.response?.data || err.message,
      errorMessage:
        err.response?.data?.description ||
        err.response?.data?.message ||
        'Ошибка создания платежа',
    };
  }
}

export async function prepaymentBitrixPaymentObjectSend(
  guid: string,
  items: IBitrixItem[],
  grunt: boolean,
  paymentId: string
): Promise<void> {
  try {
    const partnerId = getPartnerIdFromStorage();

    if (!partnerId) {
      throw new Error('PartnerId is missing. User must be logged in to process payment.');
    }

    const changeDate = getMoscowDate();

    const guidPrepayment = generateGuid({
      paymentObject: {
        partnerId: partnerId,
        isCourtChange: grunt,
        guid,
        changeDate,
        items,
      },
    });

    const paymentObject = {
      partnerId: partnerId,
      isCourtChange: grunt,
      guid,
      changeDate,
      items,
      paymentId: paymentId,
    };

    const paymentObjectString = JSON.stringify(paymentObject);

    const data = {
      paymentObject: paymentObjectString,
      paymentId: paymentId,
      guid: guidPrepayment,
    };

    await loggerSendingData({
      type: 'bitrixPrepaymentPaymentObjectSend',
      data: data,
    });

    const response = await axiosBitrixInstance.post('/WaitingPayment', data);

    await loggerSendingData({
      type: 'bitrixPrepaymentPaymentObjectSendSuccess',
      data: response.data,
    });
  } catch (error: any) {
    console.error('Ошибка отправки препэймента:', error);

    await loggerSendingData({
      type: 'bitrixPrepaymentPaymentObjectSendError',
      data: error,
    });

    throw error;
  }
}

export async function bitrixPaymentObjectSend(
  guid: string,
  items: IBitrixItem[],
  grunt: boolean,
  paymentId: string
): Promise<void> {
  try {
    const partnerId = getPartnerIdFromStorage();

    if (!partnerId) {
      console.warn('PartnerId is missing. Skipping Bitrix payment object send.');
      return;
    }

    const changeDate = getMoscowDate();

    const data = {
      partnerId: partnerId,
      isCourtChange: grunt,
      guid,
      changeDate,
      items,
      paymentId: paymentId,
    };

    await loggerSendingData({
      type: 'bitrixPaymentObjectSend',
      data: data,
    });

    await axiosBitrixInstance.post('/CreatePayment', data);

    await loggerSendingData({
      type: 'bitrixPaymentObjectSendSuccess',
      data: { success: true },
    });

    await sendBackendPaymentInfo({ data: data, isError: false });
  } catch (error: any) {
    console.error('Ошибка отправки данных в Bitrix:', error);

    await loggerSendingData({
      type: 'bitrixPaymentObjectSendError',
      data: error,
    });

    const partnerId = getPartnerIdFromStorage();
    const changeDate = getMoscowDate();

    const data = {
      partnerId: partnerId || '',
      isCourtChange: grunt,
      guid,
      changeDate,
      items,
      paymentId: paymentId,
    };

    await sendBackendPaymentInfo({ data: data, isError: true });

    let safeErrorData = 'Unable to serialize error data';
    try {
      safeErrorData = JSON.stringify(error.response?.data);
    } catch (e) {
      safeErrorData = 'Error data serialization failed';
    }

    await feedbackEmail({
      message: `
        Ошибка отправки данных в битрикс:
        Ошибка: ${error.name || 'Unknown error'}
        Данные ошибки: ${safeErrorData}
        Сообщение ошибки: ${error.message}
        Данные в момент оплаты: ${JSON.stringify(data)}
        server_send_isError: true
      `,
    });

    throw error;
  }
}

export async function sendBackendPaymentInfo(data: {
  data: any;
  isError: boolean;
}): Promise<void> {
  try {
    await loggerSendingData({
      type: 'sendBackendPaymentInfo',
      data: data,
    });

    await axiosInstance.post('/orders', data);

    await loggerSendingData({
      type: 'sendBackendPaymentInfoSuccess',
      data: { success: true },
    });
  } catch (error: any) {
    console.error('Ошибка отправки данных на backend:', error);

    await loggerSendingData({
      type: 'sendBackendPaymentInfoError',
      data: error,
    });

    await feedbackEmail({
      message: `
        Ошибка отправки данных на сервер:
        Ошибка: ${error}
        Данные ошибки: ${JSON.stringify(error.response?.data)}
        Сообщение ошибки: ${error.message}
        Данные в момент оплаты: ${JSON.stringify(data)}
      `,
    });
  }
}

export async function cancelPayment(paymentId: string): Promise<{ success: boolean; data?: any; error?: any }> {
  try {
    await loggerSendingData({ type: 'cancelPaymentAttempt', data: { paymentId } });

    const response = await axiosInstance.post(`/payments/${paymentId}/cancel`);

    await loggerSendingData({ type: 'cancelPaymentSuccess', data: response.data });

    return { success: true, data: response.data };
  } catch (err: any) {
    console.error('Ошибка отмены платежа:', err.response?.data || err.message);

    await loggerSendingData({ type: 'cancelPaymentError', data: err.response?.data || err.message });

    return { success: false, error: err.response?.data || err.message };
  }
}

export async function getPaymentInfo(paymentId: string): Promise<any> {
  try {
    const response = await axiosInstance.get(`/payments/${paymentId}/status`);
    return response.data;
  } catch (err: any) {
    console.error('Ошибка при получении информации о платеже:', err.response?.data || err.message);

    await feedbackEmail({
      message: `
        Ошибка при получении информации о платеже:
        Ошибка: ${err}
        Данные ошибки: ${JSON.stringify(err.response?.data)}
        Сообщение ошибки: ${err.message}
      `,
    });

    return null;
  }
}
